import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Shield, ArrowLeft, ArrowRight, Loader2, Repeat, Wallet, Users,
  Star, Briefcase, DollarSign,
} from 'lucide-react';
import { submitTrainerApplication } from '../lib/api';
import TrainerEarningsBreakdown from '../components/TrainerEarningsBreakdown';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const DISCIPLINES = [
  { id: 'jiujitsu', label: 'Brazilian Jiu-Jitsu' },
  { id: 'self_defense', label: 'Self-Defense' },
  { id: 'personal_training', label: 'Personal Training' },
];

const VALUE_POINTS = [
  { icon: Repeat, title: 'Retain clients.', body: null },
  { icon: Wallet, title: 'Get paid fast.', body: 'Direct to your bank account after every session. No subscriptions, no monthly fees, no waiting on invoices.' },
  { icon: Users, title: 'Clients come to you.', body: 'Stop cold outreaching and posting into the void. We put your profile in front of people already looking for what you teach.' },
  { icon: Star, title: 'Your reputation compounds here.', body: 'Every verified review you earn lives on your profile permanently. The longer you stay, the harder you are to compete with.' },
  { icon: Briefcase, title: 'Your entire business in one place.', body: 'Schedule, client history, payments, and your full book of business managed from your phone.' },
  { icon: DollarSign, title: 'You set the price. We don\u2019t touch it.', body: 'BJJ, self-defense, personal training. Each service priced independently by you. No platform-set rates, no race to the bottom.' },
];

const BecomeATrainer = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('pitch'); // 'pitch' | 'form' | 'done'
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    disciplines: [],
    credentials: '',
    years_experience: '',
    bio: '',
    social_links: '',
  });
  const [bgAuthorized, setBgAuthorized] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleDiscipline = (d) => {
    setForm((f) => ({
      ...f,
      disciplines: f.disciplines.includes(d)
        ? f.disciplines.filter((x) => x !== d)
        : [...f.disciplines, d],
    }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.full_name.trim()) { toast.error('Full name is required'); return; }
    if (!form.email.trim()) { toast.error('Email is required'); return; }
    if (form.disciplines.length === 0) { toast.error('Select at least one discipline'); return; }
    if (!form.credentials.trim()) { toast.error('Credentials are required'); return; }
    const years = Number(form.years_experience);
    if (!Number.isFinite(years) || years < 0) { toast.error('Enter valid years of experience'); return; }
    if (!form.bio.trim() || form.bio.length > 300) {
      toast.error('Bio is required and must be 300 characters or fewer'); return;
    }
    if (!bgAuthorized) {
      toast.error('You must agree to the Background Check Disclosure and Authorization'); return;
    }

    setSubmitting(true);
    try {
      await submitTrainerApplication({
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: (form.phone || '').trim() || 'not provided',
        city: 'not provided',
        disciplines: form.disciplines,
        credentials: form.credentials.trim() + (form.social_links.trim() ? `\nSocial: ${form.social_links.trim()}` : ''),
        years_experience: Math.round(years),
        bio: form.bio.trim(),
        background_check_authorization: true,
      });
      setPhase('done');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not submit application';
      toast.error(typeof msg === 'string' ? msg : 'Could not submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === 'done') {
    return (
      <div className="min-h-screen bg-[#09090B] py-16 px-4 sm:px-6 lg:px-8 flex items-center" data-testid="application-done">
        <div className="max-w-xl mx-auto bg-[#18181B] border border-zinc-800 rounded-lg p-12 text-center">
          <div className="w-12 h-12 rounded-full bg-[#C0A062]/10 border border-[#C0A062]/30 flex items-center justify-center mx-auto mb-7">
            <Shield className="w-6 h-6 text-[#C0A062]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">You&apos;re in.</h1>
          <p className="text-zinc-400 mb-2">
            Create your account, finish your profile, and you&apos;ll be live to receive bookings.
          </p>
          <p className="text-xs text-zinc-500 mb-10">
            No waiting on approval. Your profile goes live the moment the 6-step setup is complete.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              onClick={() => navigate('/register?role=trainer')}
              className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold"
              data-testid="create-account-btn"
            >
              Create my account
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              data-testid="back-home-btn"
            >
              Back home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] py-12 px-4 sm:px-6 lg:px-8" data-testid="become-trainer-page">
      <SEO
        title="Coach on DojoDash | Get Paid to Train"
        description="Join DojoDash as a professional coach. Set your rates, keep 80% of every session, and get paid fast."
      />
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center text-zinc-400 hover:text-white text-sm mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back home
        </Link>

        {phase === 'pitch' && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-8 sm:p-10" data-testid="trainer-pitch">
            <div className="flex items-center gap-2 mb-6">
              <Shield className="w-6 h-6 text-[#C0A062]" />
              <span className="font-accent text-[#C0A062] tracking-widest text-xs">FOR COACHES</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3" data-testid="pitch-headline">
              Here&apos;s what you get on DojoDash:
            </h1>

            <ul className="space-y-5 my-8" data-testid="coach-benefits">
              {VALUE_POINTS.map(({ icon: Icon, title, body }, i) => (
                <li key={i} className="flex gap-4" data-testid={`coach-benefit-${i + 1}`}>
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-[#C0A062]/10 border border-[#C0A062]/30 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#C0A062]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{title}</p>
                    {body && <p className="text-zinc-400 text-sm mt-1">{body}</p>}
                  </div>
                </li>
              ))}
            </ul>

            <p className="text-xs text-zinc-500 leading-relaxed border-t border-zinc-800 pt-5 mb-8" data-testid="off-platform-notice">
              Off-platform bookings with DojoDash clients are a violation of our Trainer Terms and may result
              in removal from the platform.
            </p>

            {/* Transparent earnings breakdown */}
            <div className="mb-8">
              <TrainerEarningsBreakdown defaultRate={75} variant="pitch" />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => setPhase('form')}
                className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold flex-1"
                data-testid="pitch-continue-btn"
              >
                Join As A Coach
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Not yet
              </Button>
            </div>
          </div>
        )}

        {phase === 'form' && (
          <form
            onSubmit={submit}
            className="bg-[#18181B] border border-zinc-800 rounded-lg p-8 sm:p-10"
            data-testid="trainer-application-form"
          >
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Become a DojoDash coach</h1>
            <p className="text-sm text-zinc-400 mb-8">
              Approval is instant — complete the form and you can start setting up your coach profile right away. Tell us about yourself.
            </p>

            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Full name *</Label>
                  <Input
                    value={form.full_name}
                    onChange={(e) => setField('full_name', e.target.value)}
                    className="bg-[#09090B] border-zinc-800 text-white mt-1.5"
                    data-testid="app-full-name"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                    className="bg-[#09090B] border-zinc-800 text-white mt-1.5"
                    data-testid="app-email"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Phone (optional)</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setField('phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="bg-[#09090B] border-zinc-800 text-white mt-1.5"
                    data-testid="app-phone"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Years of experience *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.years_experience}
                    onChange={(e) => setField('years_experience', e.target.value)}
                    className="bg-[#09090B] border-zinc-800 text-white mt-1.5"
                    data-testid="app-years"
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 mb-2 block">Disciplines (select at least one) *</Label>
                <div className="flex flex-wrap gap-2" data-testid="app-disciplines">
                  {DISCIPLINES.map((d) => {
                    const active = form.disciplines.includes(d.id);
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => toggleDiscipline(d.id)}
                        className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                          active
                            ? 'bg-[#C0A062] border-[#C0A062] text-black font-semibold'
                            : 'bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800'
                        }`}
                        data-testid={`app-discipline-${d.id}`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-zinc-300">Credentials and certifications *</Label>
                <Input
                  value={form.credentials}
                  onChange={(e) => setField('credentials', e.target.value)}
                  placeholder="e.g. BJJ Black Belt under Vagner Rocha, NASM CPT"
                  className="bg-[#09090B] border-zinc-800 text-white mt-1.5"
                  data-testid="app-credentials"
                />
              </div>

              <div>
                <Label className="text-zinc-300">
                  Short bio *{' '}
                  <span className="text-zinc-500 text-xs">({form.bio.length}/300)</span>
                </Label>
                <Textarea
                  rows={4}
                  value={form.bio}
                  onChange={(e) => setField('bio', e.target.value.slice(0, 300))}
                  placeholder="Tell us about your training style, results, and who you love coaching."
                  className="bg-[#09090B] border-zinc-800 text-white mt-1.5"
                  data-testid="app-bio"
                />
              </div>

              <div>
                <Label className="text-zinc-300">Social media links (optional)</Label>
                <Input
                  value={form.social_links}
                  onChange={(e) => setField('social_links', e.target.value)}
                  placeholder="instagram.com/yourhandle, youtube.com/yourchannel"
                  className="bg-[#09090B] border-zinc-800 text-white mt-1.5"
                  data-testid="app-social"
                />
              </div>
            </div>

            <label className="flex items-start gap-3 mt-8 mb-3 cursor-pointer" data-testid="bg-check-auth-label">
              <input
                type="checkbox"
                checked={bgAuthorized}
                onChange={(e) => setBgAuthorized(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-zinc-700 bg-[#09090B] accent-[#C0A062] cursor-pointer"
                data-testid="bg-check-auth-checkbox"
              />
              <span className="text-xs text-zinc-400 leading-relaxed">
                I have read and agree to the{' '}
                <Link to="/terms" target="_blank" className="text-[#C0A062] hover:underline">
                  Background Check Disclosure and Authorization
                </Link>
                , and I authorize DojoDash to obtain a background check report through Checkr for
                the purpose of verifying my eligibility to coach on the platform.
              </span>
            </label>

            <p className="text-xs text-zinc-500 mb-6">
              By submitting, you agree to our{' '}
              <Link to="/terms" className="text-zinc-400 hover:text-white underline">
                Trainer Terms
              </Link>
              , including the prohibition on off-platform payments.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold flex-1"
                data-testid="app-submit-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Join As A Coach
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPhase('pitch')}
                disabled={submitting}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Back
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BecomeATrainer;
